import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Divider,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Language as LanguageIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from './api';
import { AVAILABLE_LANGUAGES } from './utils/languages';

// Consistent card styling
const cardStyle = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'box-shadow 0.3s ease-in-out',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  },
};

const cardMediaStyle = {
  height: 300,
  objectFit: 'contain',
  bgcolor: 'background.default',
  p: 2
};

const cardContentStyle = {
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
};

export default function CategoryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('english');
  const [savingChanges, setSavingChanges] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, category: null });
  const [deleting, setDeleting] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  
  // Default languages
  const defaultLanguages = AVAILABLE_LANGUAGES;
  
  // Fetch category details on component mount
  useEffect(() => {
    if (id) {
      fetchCategoryDetails(id);
    }
  }, [id]);
  
  // Fetch category details by ID
  const fetchCategoryDetails = async (categoryId) => {
    setLoading(true);
    try {
      const response = await api.get(`/category/${categoryId}`);
      
      if (response.data && response.data.success) {
        const categoryDetail = response.data.data;
        
        // Parse languageSpec if it's a string
        if (typeof categoryDetail.languageSpec === 'string') {
          categoryDetail.languageSpec = JSON.parse(categoryDetail.languageSpec);
        }
        
        setCategory(categoryDetail);
        setEditMode(false);
      } else {
        console.error('Category not found or error in response');
      }
    } catch (error) {
      console.error('Error fetching category details:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Enable edit mode
  const handleEditClick = () => {
    setEditMode(true);
  };
  
  // Cancel edit mode
  const handleCancelEdit = () => {
    setEditMode(false);
    // Reset any changes by re-fetching the category
    fetchCategoryDetails(id);
  };
  
  // Navigate back to category search
  const handleBackToSearch = () => {
    navigate('/categories');
  };
  
  // Save language-specific name changes
  const handleSaveLanguageName = async () => {
    setSavingChanges(true);
    try {
      // Get the current language data
      const languageSpec = category.languageSpec || {};
      const currentLanguageData = languageSpec[currentLanguage] || {};
      const newName = currentLanguageData.name || '';
      
      const response = await api.put(`/category/update-category/${category.id}`, {
        language: currentLanguage,
        name: newName
      });
      
      if (response.data && response.data.success) {
        // Parse languageSpec if it's a string
        const updatedCategory = response.data.data;
        if (typeof updatedCategory.languageSpec === 'string') {
          updatedCategory.languageSpec = JSON.parse(updatedCategory.languageSpec);
        }
        
        setEditMode(false);
        setCategory(updatedCategory);
      } else {
        console.error('Failed to update category item');
      }
    } catch (error) {
      console.error('Error saving changes:', error);
    } finally {
      setSavingChanges(false);
    }
  };

  // Handle delete category click
  const handleDeleteClick = () => {
    setDeleteDialog({ open: true, category: category });
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deleteDialog.category) return;
    
    setDeleting(true);
    try {
      await api.deleteCategory(deleteDialog.category.id);
      
      // Show success notification
      setNotification({
        open: true,
        message: `Category "${deleteDialog.category.name}" deleted successfully!`,
        severity: 'success'
      });
      
      // Close dialog and navigate back to categories
      setDeleteDialog({ open: false, category: null });
      navigate('/categories');
      
    } catch (error) {
      console.error('Error deleting category:', error);
      
      // Extract error message from the API response
      let errorMessage = 'Failed to delete category. Please try again.';
      if (error.response?.data?.error?.userMessage) {
        errorMessage = error.response.data.error.userMessage;
      } else if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setNotification({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setDeleting(false);
    }
  };

  // Handle delete dialog close
  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, category: null });
  };

  // Handle notification close
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };
  
  // Handle language name input change
  const handleLanguageNameChange = (value) => {
    const updatedLanguageSpec = { ...category.languageSpec } || {};
    
    if (!updatedLanguageSpec[currentLanguage]) {
      updatedLanguageSpec[currentLanguage] = {};
    }
    
    updatedLanguageSpec[currentLanguage].name = value;
    
    setCategory({
      ...category,
      languageSpec: updatedLanguageSpec
    });
  };
  
  // Handle language image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('language', currentLanguage);
      
      const response = await api.put(`/upload-image/upload-category-image/${category.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data && response.data.success) {
        // Parse languageSpec if it's a string
        const updatedCategory = response.data.data;
        if (typeof updatedCategory.languageSpec === 'string') {
          updatedCategory.languageSpec = JSON.parse(updatedCategory.languageSpec);
        }
        
        setCategory(updatedCategory);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setUploadingImage(false);
    }
  };
  
  // Handle language change
  const handleLanguageChange = (event) => {
    setCurrentLanguage(event.target.value);
  };
  
  // Get the display name for the current category based on selected language
  const getCategoryDisplayName = () => {
    if (!category) return '';
    
    const languageSpec = category.languageSpec || {};
    
    // Use the language-specific name if available, otherwise fall back to default name
    return (languageSpec[currentLanguage]?.name) || category.name;
  };
  
  // Get available languages for the category
  const getAvailableLanguages = () => {
    if (!category || !category.languageSpec) return [];
    
    return Object.keys(category.languageSpec).filter(lang => 
      category.languageSpec[lang]?.name || category.languageSpec[lang]?.image
    );
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (!category) {
    return (
      <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5" color="error">Category not found</Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToSearch}
          sx={{ mt: 2 }}
        >
          Back to Search
        </Button>
      </Paper>
    );
  }
  
  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link 
          component="button"
          underline="hover"
          sx={{ display: 'flex', alignItems: 'center' }}
          onClick={() => navigate('/')}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Home
        </Link>
        <Link
          component="button"
          underline="hover"
          sx={{ display: 'flex', alignItems: 'center' }}
          onClick={handleBackToSearch}
        >
          Category Search
        </Link>
        <Typography color="text.primary">{category.name}</Typography>
      </Breadcrumbs>
      
      {/* Main Content */}
      <Paper elevation={3} sx={{ p: 3 }}>
        {!editMode ? (
          // View Mode
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
              <Typography variant="h4">{getCategoryDisplayName()}</Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={handleEditClick}
                >
                  Edit Category
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteClick}
                >
                  Delete
                </Button>
              </Box>
            </Box>
            
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={cardStyle} elevation={2}>
                  <CardMedia
                    component="img"
                    sx={cardMediaStyle}
                    image={category?.image || "/api/placeholder/400/320"}
                    alt={getCategoryDisplayName()}
                  />
                  <CardContent sx={cardContentStyle}>
                    <Typography variant="h6" gutterBottom>Category Details</Typography>
                    <Typography variant="body1"><strong>ID:</strong> {category.id}</Typography>
                    <Typography variant="body1"><strong>Default Name:</strong> {category.name}</Typography>
                    {category.description && (
                      <Typography variant="body1" sx={{ mt: 1 }}>
                        <strong>Description:</strong> {category.description}
                      </Typography>
                    )}
                    
                    <Box sx={{ mt: 'auto', pt: 2 }}>
                      <Typography variant="h6" gutterBottom>Available Languages</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {getAvailableLanguages().map(lang => (
                          <Chip
                            key={lang}
                            label={lang.charAt(0).toUpperCase() + lang.slice(1)}
                            color={currentLanguage === lang ? "primary" : "default"}
                            variant={currentLanguage === lang ? "filled" : "outlined"}
                            icon={<LanguageIcon />}
                            onClick={() => setCurrentLanguage(lang)}
                          />
                        ))}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card sx={cardStyle} elevation={2}>
                  <CardContent sx={cardContentStyle}>
                    <Typography variant="h6" gutterBottom>Language Versions</Typography>
                    
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>Select Language</InputLabel>
                      <Select
                        value={currentLanguage}
                        label="Select Language"
                        onChange={handleLanguageChange}
                      >
                        {defaultLanguages.map((lang) => (
                          <MenuItem key={lang} value={lang}>
                            {lang.charAt(0).toUpperCase() + lang.slice(1)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        {currentLanguage.charAt(0).toUpperCase() + currentLanguage.slice(1)} Version
                      </Typography>
                      
                      <Typography variant="body1">
                        <strong>Name:</strong> {category.languageSpec?.[currentLanguage]?.name || 'Not set'}
                      </Typography>
                    </Box>
                    
                    {category.languageSpec?.[currentLanguage]?.image ? (
                      <Box sx={{ mt: 'auto' }}>
                        <Typography variant="body1" gutterBottom><strong>Image:</strong></Typography>
                        <Box sx={{ maxWidth: '100%', overflow: 'hidden', borderRadius: '4px' }}>
                          <img
                            src={category.languageSpec[currentLanguage].image}
                            alt={`${currentLanguage} category`}
                            style={{ width: '100%', maxHeight: '300px', objectFit: 'contain' }}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ 
                        mt: 'auto',
                        p: 3, 
                        bgcolor: 'background.default', 
                        borderRadius: '4px',
                        textAlign: 'center'
                      }}>
                        <Typography variant="body2" color="text.secondary">
                          No image for this language
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        ) : (
          // Edit Mode
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
              <Typography variant="h4">Edit Category: {category.name}</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveLanguageName}
                  disabled={savingChanges}
                >
                  {savingChanges ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Box>
            
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={cardStyle} elevation={2}>
                  <CardContent sx={cardContentStyle}>
                    <Typography variant="h6" gutterBottom>Language Settings</Typography>
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>Language</InputLabel>
                      <Select
                        value={currentLanguage}
                        label="Language"
                        onChange={handleLanguageChange}
                      >
                        {defaultLanguages.map((lang) => (
                          <MenuItem key={lang} value={lang}>
                            {lang.charAt(0).toUpperCase() + lang.slice(1)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                    <TextField
                      fullWidth
                      label={`Name (${currentLanguage})`}
                      value={(category.languageSpec?.[currentLanguage]?.name) || ''}
                      onChange={(e) => handleLanguageNameChange(e.target.value)}
                      sx={{ mb: 3 }}
                    />
                    
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      sx={{ height: '56px', mb: 3 }}
                      disabled={uploadingImage}
                    >
                      {uploadingImage ? 'Uploading...' : `Upload ${currentLanguage.charAt(0).toUpperCase() + currentLanguage.slice(1)} Image`}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleImageUpload}
                      />
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card sx={cardStyle} elevation={2}>
                  <CardContent sx={cardContentStyle}>
                    <Typography variant="h6" gutterBottom>
                      Current {currentLanguage.charAt(0).toUpperCase() + currentLanguage.slice(1)} Preview
                    </Typography>
                    
                    <Typography variant="body1" gutterBottom>
                      <strong>Name:</strong> {category.languageSpec?.[currentLanguage]?.name || 'Not set'}
                    </Typography>
                    
                    {category.languageSpec?.[currentLanguage]?.image ? (
                      <Box sx={{ mt: 'auto' }}>
                        <Typography variant="body1" gutterBottom><strong>Current Image:</strong></Typography>
                        <Box sx={{ maxWidth: '100%', overflow: 'hidden', borderRadius: '4px' }}>
                          <img
                            src={category.languageSpec[currentLanguage].image}
                            alt={`${currentLanguage} category preview`}
                            style={{ width: '100%', maxHeight: '300px', objectFit: 'contain' }}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ 
                        mt: 'auto',
                        p: 3, 
                        bgcolor: 'background.default', 
                        borderRadius: '4px', 
                        textAlign: 'center'
                      }}>
                        <Typography variant="body2" color="text.secondary">
                          No image uploaded for this language
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. Please make sure no food items are using this category.
          </Alert>
          <Typography>
            Are you sure you want to delete the category "{deleteDialog.category?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}